import { tenantService } from "../../../services/tenantService";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogIn } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { authService } from "../../../services";
import { toast } from "react-toastify";


export const TenantListing: React.FC = () => {
  const navigate = useNavigate();
  const {setTokenAndRefresh} =useAuth()

  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [loadingLoginAs, setLoadingLoginAs] = useState<string | null>(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const res: any = await tenantService.list();
      setTenants(res?.tenants || []);
    } catch (err) {
      console.error("Failed to load tenants", err);
    } finally {
      setLoading(false);
    }
  };

  const loginAs = async (userId: string) => {
    console.log("Login as tenant user:", userId);
    if(loadingLoginAs){
      toast.success("Already logging in as another tenant. Please wait.");
      return;
    }
    try{
      setLoadingLoginAs(userId);
      const data = await authService.loginAs({userId})
      await setTokenAndRefresh(data);
    }catch(err){
      console.error("Login as tenant failed", err);
    }finally{
      setLoadingLoginAs(null);
    }
  }

  const filteredTenants = useMemo(() => {
    return tenants.filter((t) =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tenants, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <h1 className="text-3xl font-bold">Tenant Listing</h1>
        <p className="text-gray-500">All Tenants in the System</p>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        {/* Search + View Mode */}
        <div className="bg-white p-4 rounded-xl shadow mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <input
            type="text"
            placeholder="Search tenants by name..."
            className="border rounded-lg px-4 py-2 w-full md:w-1/2"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="flex bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`px-4 py-2 rounded-lg ${
                viewMode === "grid"
                  ? "bg-white shadow text-blue-600"
                  : "text-gray-600"
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-4 py-2 rounded-lg ${
                viewMode === "list"
                  ? "bg-white shadow text-blue-600"
                  : "text-gray-600"
              }`}
            >
              List
            </button>
          </div>
        </div>

        {/* Loading / Empty State */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading tenants...</div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No tenants found.</div>
        ) : viewMode === "grid" ? (
          /* GRID VIEW */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTenants.map((tenant) => (
              <div
                key={tenant.id}
                className="bg-white shadow p-5 rounded-xl border hover:shadow-lg transition"
              >
                <h3 className="text-lg font-semibold">{tenant.name}</h3>
                <p className="text-sm text-gray-500">{tenant.slug}</p>

                <div className="mt-3 flex flex-col gap-1 text-sm">
                  <div>
                    <strong>Total Users:</strong> {tenant.count}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        tenant.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {tenant.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Created: {new Date(tenant.createdAt).toLocaleString()}
                  </div>
                </div>

                {/* Configure Button */}
               <div className="mt-3 flex justify-end gap-3">
                {/* Login As */}
                <button
                  onClick={() => loginAs(tenant.adminUser.id)}
                  className="p-2 rounded-full hover:bg-gray-100"
                  title="Login As"
                >
                  <LogIn size={20} className="text-gray-600" />
                </button>

                {/* Configure */}
                <button
                  onClick={() => navigate(`/tenant/${tenant.id}/config`)}
                  className="p-2 rounded-full hover:bg-gray-100"
                  title="Configure"
                >
                  <Settings size={20} className="text-agentic-primary-blue" />
                </button>
              </div>

              </div>
            ))}
          </div>
        ) : (
          /* LIST VIEW */
          <div className="bg-white rounded-xl shadow border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100 text-gray-600 text-sm">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Slug</th>
                  <th className="p-4 text-left">Total users</th>
                  <th className="p-4 text-left">Status</th>
                  <th className="p-4 text-left">Created At</th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="border-t hover:bg-gray-50">
                    <td className="p-4 font-medium">{tenant.name}</td>
                    <td className="p-4 text-gray-600">{tenant.slug}</td>
                    <td className="p-4">{tenant.count}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          tenant.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {tenant.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-4 text-gray-500">
                      {new Date(tenant.createdAt).toLocaleString()}
                    </td>

                    {/* Configure Button */}
                    <td className="p-4 flex items-center gap-3">
                    {/* Login As */}
                    <button
                      onClick={() => loginAs(tenant.adminUser.id)}
                      className="p-2 rounded-full hover:bg-gray-100"
                      title="Login As"
                    >
                      <LogIn size={20} className="text-gray-600" />
                    </button>

                    {/* Configure */}
                    <button
                      onClick={() => navigate(`/tenant/${tenant.id}/config`)}
                      className="p-2 rounded-full hover:bg-gray-100"
                      title="Configure"
                    >
                      <Settings size={20} className="text-agentic-primary-blue" />
                    </button>
                  </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantListing;
